import React, { useState, useEffect } from 'react';
import { Table, Button, Spin, Input, Tabs, Space, Tooltip } from 'antd';
import { LoadingOutlined, SearchOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import './view.css';
import type { TableColumnsType } from 'antd';
import { getProjectTypes, getProjectTypeGroups } from '../../api/project_type';
import { ASSET_BASE_URL } from '../../api/axios';

const { TabPane } = Tabs;

interface ProjectType {
  id: number;
  name: string;
  display_name: string;
  logo?: string | null;
  project_group: {
    id: number;
    name: string;
    display_name: string;
  };
}

interface ProjectGroup {
  id: number;
  name: string;
  display_name: string;
}

const projectTypeGroupColumns: TableColumnsType<ProjectGroup> = [
  {
    title: 'Name',
    dataIndex: 'display_name',
    key: 'name'
  }
];

const ViewProjectTypeMetaComponent: React.FC<{ activeTab?: string }> = ({ activeTab }) => {
  const defaultActiveKey = activeTab === 'project_groups' ? '2' : '1';
  const [loading, setLoading] = useState<boolean>(false);
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [projectGroups, setProjectGroups] = useState<ProjectGroup[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const navigate = useNavigate();

  const missingLogoCount = projectTypes.filter((projectType) => !projectType.logo).length;

  const projectTypeColumns: TableColumnsType<ProjectType> = [
    {
      title: (
        <Space size={4}>
          Logo
          <Tooltip title={`${missingLogoCount} of ${projectTypes.length} project types missing a logo`}>
            <InfoCircleOutlined />
          </Tooltip>
        </Space>
      ),
      key: 'logo',
      sorter: (a: ProjectType, b: ProjectType) => Number(!!a.logo) - Number(!!b.logo),
      render: (_, record: ProjectType) =>
        record.logo ? (
          <img src={`${ASSET_BASE_URL}/${record.logo}`} alt="" style={{ height: 24 }} />
        ) : (
          '—'
        )
    },
    {
      title: 'Name',
      dataIndex: 'display_name',
      key: 'name',
      sorter: (a: ProjectType, b: ProjectType) =>
        (a.display_name || a.name || '').localeCompare(b.display_name || b.name || ''),
      render: (text: string, record: ProjectType) => text || record.name || 'Unnamed'
    },
    {
      title: 'Project Group',
      key: 'project_group',
      sorter: (a: ProjectType, b: ProjectType) => {
        const aGroup = a.project_group?.display_name || a.project_group?.name || '';
        const bGroup = b.project_group?.display_name || b.project_group?.name || '';
        return aGroup.localeCompare(bGroup);
      },
      render: (_, record: ProjectType) => {
        const group = record.project_group;
        return group?.display_name || group?.name || 'No Group';
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/esr/project-template/${record.id}`);
            }}
          >
            Edit ESR Fallback Text
          </Button>
        </Space>
      )
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const projectTypesData = await getProjectTypes();
        setProjectTypes(projectTypesData.response);
        const projectGroupsData = await getProjectTypeGroups();
        setProjectGroups(projectGroupsData.response);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const searchTerms = searchText.trim().toLowerCase().split(/\s+/).filter(Boolean);

  const matchesSearch = (...fields: (string | null | undefined)[]) => {
    if (!searchTerms.length) return true;
    const haystack = fields
      .filter(Boolean)
      .map((field) => String(field).toLowerCase().replace(/_/g, ' '))
      .join(' ');
    return searchTerms.every((term) => haystack.includes(term));
  };

  const filteredProjectTypes = projectTypes.filter((projectType) =>
    matchesSearch(
      projectType.display_name,
      projectType.name,
      projectType.project_group?.display_name,
      projectType.project_group?.name,
    )
  );

  const filteredProjectGroups = projectGroups.filter((projectGroup) =>
    matchesSearch(projectGroup.display_name, projectGroup.name)
  );

  return (
    <div className="table-container">
      <Spin
        spinning={loading}
        indicator={<LoadingOutlined style={{ fontSize: 24, color: 'white' }} spin />}
        fullscreen
      />
      <Tabs defaultActiveKey={defaultActiveKey}>
        <TabPane tab="Project Types" key="1">
          <div className="table-controls">
            <div>
              <Input
                prefix={<SearchOutlined />}
                placeholder="Search Project Type"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ marginBottom: '16px', width: '50%' }}
              />
            </div>
            <Link to="/project_types/create">
              <Button type="primary" icon={<PlusOutlined />} style={{ marginRight: '8px' }}>
                Create Project Type
              </Button>
            </Link>
          </div>
          <Table
            columns={projectTypeColumns}
            dataSource={filteredProjectTypes.map((projectType) => ({
              ...projectType,
              key: projectType.id
            }))}
            showSorterTooltip={false}
            onRow={(record: ProjectType) => ({
              onClick: () => navigate(`/project_types/edit/${record.id}`),
              style: { cursor: 'pointer' }
            })}
          />
        </TabPane>
        <TabPane tab="Project Groups" key="2">
          <div className="table-controls">
            <div>
              <Input
                prefix={<SearchOutlined />}
                placeholder="Search Project Group"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ marginBottom: '16px', width: '50%' }}
              />
            </div>
            <Link to="/project_groups/create">
              <Button type="primary" icon={<PlusOutlined />} style={{ marginRight: '8px' }}>
                Create Project Group
              </Button>
            </Link>
          </div>
          <Table
            columns={projectTypeGroupColumns}
            dataSource={filteredProjectGroups.map((projectGroup) => ({
              ...projectGroup,
              key: projectGroup.id
            }))}
            onRow={(record: ProjectGroup) => ({
              onClick: () => navigate(`/project_groups/edit/${record.id}`),
              style: { cursor: 'pointer' }
            })}
          />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default ViewProjectTypeMetaComponent;
